<template>
  <div class="container">
    <div :class="`alert ${step === 0 ? 'alert-dark' : 'alert-light'}`">
      <h4>Main particulars</h4>
    </div>
    <div v-show="step === 0">
      <form>
        <form-range
          id="loa"
          tag="L<sub>OA</sub> (m)"
          descr="Length over all"
          v-bind:value="ship.particulars.Loa"
          v-on:update:value="ship.particulars.Loa = $event"
          :vMin="vRange.LoaMin"
          :vMax="vRange.LoaMax"
          :step="0.1"
        />
        <form-range
          id="lpp"
          tag="L<sub>PP</sub> (m)"
          descr="Length between perpendiculars"
          v-bind:value="ship.particulars.Lpp"
          v-on:update:value="ship.particulars.Lpp = $event"
          :vMin="vRange.LppMin"
          :vMax="vRange.LppMax"
          :step="0.1"
        />
        <form-range
          id="lwl"
          tag="L<sub>WL</sub> (m)"
          descr="Waterline length"
          v-bind:value="ship.particulars.Lwl"
          v-on:update:value="ship.particulars.Lwl = $event"
          :vMin="vRange.LwlMin"
          :vMax="vRange.LwlMax"
          :step="0.1"
        />
        <form-range
          id="b"
          tag="B (m)"
          descr="Beam"
          v-bind:value="ship.particulars.B"
          v-on:update:value="ship.particulars.B = $event"
          :vMin="vRange.BMin"
          :vMax="vRange.BMax"
          :step="0.1"
        />
        <form-range
          id="t"
          tag="T <sub>scant</sub>(m)"
          descr="Scantling draught"
          v-bind:value="ship.particulars.T"
          v-on:update:value="ship.particulars.T = $event"
          :vMin="vRange.TMin"
          :vMax="vRange.TMax"
          :step="0.1"
        />
        <form-range
          id="t ballast"
          tag="T<sub>ballast</sub> (m)"
          descr="Ballast draught"
          v-bind:value="ship.particulars.Tballast"
          v-on:update:value="ship.particulars.Tballast = $event"
          :vMin="vRange.TballastMin"
          :vMax="vRange.TballastMax"
          :step="0.1"
        />
        <form-range
          id="d"
          tag="D (m)"
          descr="Depth/top deck height"
          v-bind:value="ship.particulars.D"
          v-on:update:value="ship.particulars.D = $event"
          :vMin="vRange.DMin"
          :vMax="vRange.DMax"
          :step="0.1"
        />
        <form-range
          id="Cb"
          tag="C<sub>b</sub> (-)"
          descr="Block coefficient"
          v-bind:value="ship.particulars.Cb"
          v-on:update:value="ship.particulars.Cb = $event"
          :vMin="0.65"
          :vMax="0.9"
          :step="0.01"
        />
        <form-range
          id="vmax"
          tag="V<sub>max</sub> (knots)"
          descr="Maximum ship speed"
          v-bind:value="ship.particulars.Vmax"
          v-on:update:value="ship.particulars.Vmax = $event"
          :vMin="10"
          :vMax="30"
          :step="0.1"
        />
        <form-range
          id="pme"
          tag="P<sub>ME</sub> total (kW)"
          descr="Total installed propulsion power"
          v-bind:value="ship.particulars.Pme"
          v-on:update:value="ship.particulars.Pme = $event"
          :vMin="4000"
          :vMax="100000"
          :step="1000"
        />       
        <form-range
          id="dprop"
          tag="D<sub>prop</sub> (m)"
          descr="Propeller diameter"
          v-bind:value="ship.propeller.D"
          v-on:update:value="ship.propeller.D = $event"
          :vMin="vRange.DpropMin"
          :vMax="vRange.DpropMax"
          :step="0.01"
        />
        <form-range
          id="nshaft"
          tag="N<sub>shaft</sub> (-)"
          descr="Number of propeller shafts"
          v-bind:value="ship.propeller.N"
          v-on:update:value="ship.propeller.N = $event"
          :vMin="1"
          :vMax="2"
          :step="1"
        />
      <div class="row">
        <div class="col-6"></div>
        <div class="col-6"><button @click.prevent="onStepChange(1)"  class="btn btn-dark pull-right">next &gt;&gt;</button></div>      
      </div>
      </form>
    </div>
    <div :class="`alert ${step === 1 ? 'alert-dark' : 'alert-light'}`">
      <h4>Additional particulars</h4>
    </div>
    <div v-show="step === 1">
      <form>
        <form-range
          id="s"
          tag="S (m<sup>2</sup>)"
          descr="Wetted surface area"
          v-bind:value="ship.particulars.S"
          v-on:update:value="ship.particulars.S = $event"
          :vMin="vRange.SMin"
          :vMax="vRange.SMax"
          :step="1"
        />
        <form-range
          id="iE"
          tag="i<sub>E</sub> (&deg;)"
          descr="Half waterline entrance angle"
          v-bind:value="ship.particulars.iE"
          v-on:update:value="ship.particulars.iE = $event"
          :vMin="20"
          :vMax="90"
          :step="1"
        />
      </form>
      <div class="row">
        <div class="col-6"><button @click.prevent="onStepChange(-1)" class="btn btn-light pull-left">&lt;&lt; previous</button></div>
        <div class="col-6"><button @click.prevent="onStepChange(1)"  class="btn btn-dark pull-right">next &gt;&gt;</button></div>      
      </div>
    </div>
    <div :class="`alert ${step === 2 ? 'alert-dark' : 'alert-light'}`">
      <h4>Powering</h4>
    </div>
    <div v-show="step === 2">
      <table>
        <thead>
          <tr>
            <td>#</td>
            <td>V<sub>s</sub> (knots)</td>
            <td>Ps (kW)</td>
            <td>w (-)</td>
            <td>t (-)</td>
            <td>&eta;<sub>R</sub> (-)</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in ship.speedPower">
            <td>{{i+1}}</td>
            <td>
              <input
                @change="row.speed = parseFloat($event.target.value).toFixed(3)"
                v-on:paste.prevent="onPaste(i,0,$event)"
                style="width: 100%;"
                :value="row.speed"
              />
            </td>
            <td>
              <input
                @change="row.power = parseFloat($event.target.value).toFixed(3)"
                v-on:paste.prevent="onPaste(i,1,$event)"
                style="width: 100%;"
                :value="row.power"
              />
            </td>
            <td>
              <input
                @change="row.w = parseFloat($event.target.value).toFixed(3)"
                v-on:paste.prevent="onPaste(i,2,$event)"
                style="width: 100%;"
                :value="row.w"
              />
            </td>
            <td>
              <input
                @change="row.t = parseFloat($event.target.value).toFixed(3)"
                v-on:paste.prevent="onPaste(i,3,$event)"
                style="width: 100%;"
                :value="row.t"
              />
            </td>
            <td>
              <input
                @change="row.eta_r = parseFloat($event.target.value).toFixed(3)"
                v-on:paste.prevent="onPaste(i,4,$event)"
                style="width: 100%;"
                :value="row.eta_R"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div class="row">
        <div class="col-6"><button @click.prevent="onStepChange(-1)" class="btn btn-light pull-left">&lt;&lt; previous</button></div>
        <div class="col-6"><button @click.prevent="onStepChange(1)"  class="btn btn-dark pull-right">next &gt;&gt;</button></div>      
      </div>
    </div>
    <div :class="`alert ${step === 3 ? 'alert-dark' : 'alert-light'}`">
      <h4>Propeller</h4>
    </div>
    <div v-show="step === 3">
      <table>
        <thead>
          <tr>
            <td>#</td>
            <td>J (-)</td>
            <td>K<sub>t</sub> (-)</td>
            <td>K<sub>q</sub> (-)</td>
            <td>&eta;<sub>0</sub> (-)</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in ship.propeller.curves">
            <td>{{i+1}}</td>
            <td>
              <input v-on:paste.prevent="onPaste(i,0,$event)" style="width: 100%;" v-model="row.J" />
            </td>
            <td>
              <input v-on:paste.prevent="onPaste(i,1,$event)" style="width: 100%;" v-model="row.Kt" />
            </td>
            <td>
              <input v-on:paste.prevent="onPaste(i,2,$event)" style="width: 100%;" v-model="row.Kq" />
            </td>
            <td>{{(row.J*row.Kt)/(2*Math.PI*row.Kq) | round(2) }}</td>
          </tr>
        </tbody>
      </table>
      <div class="row">
        <div class="col-6"><button @click.prevent="onStepChange(-1)" class="btn btn-light pull-left">&lt;&lt; previous</button></div>
        <div class="col-6"><button @click.prevent="onStepChange(1)"  class="btn btn-dark pull-right">next &gt;&gt;</button></div>      
      </div>
    </div>

    <div :class="`alert ${step === 4 ? 'alert-dark' : 'alert-light'}`">
      <h4>Fuel consumption</h4>
    </div>
    <div v-show="step === 4">
      <table>
        <thead>
          <tr>
            <td>#</td>
            <td>Load (%)</td>
            <td>SFOC (g/kWh)</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in ship.sfoc.mainEngine">
            <td>{{i+1}}</td>
            <td>
              <input v-on:paste.prevent="onPaste(i,0,$event)" style="width: 100%;" v-model="100*row.load" />
            </td>
            <td>
              <input v-on:paste.prevent="onPaste(i,1,$event)" style="width: 100%;" v-model="row.sfoc" />
            </td>
          </tr>
        </tbody>
      </table>
      <form>
        <form-range
          id="auxsfoc"
          tag="SFOC<sub>aux</sub> (g/kWh)"
          descr="Auxilary engine SFOC "
          v-bind:value="ship.sfoc.aux"
          v-on:update:value="ship.sfoc.aux = $event"
          :vMin="170"
          :vMax="250"
          :step="1.0"
        />
      </form>
      <div class="row">
        <div class="col-6"><button @click.prevent="onStepChange(-1)" class="btn btn-light pull-left">&lt;&lt; previous</button></div>
        <div class="col-6"><button @click.prevent="onStepChange(1)"  class="btn btn-dark pull-right">next &gt;&gt;</button></div>      
      </div>
    </div>
  </div>
</template>


<script>
import FormRange from "./FormRange.vue";
import axios from "axios";

export default {
  name: "ShipEditor",
  components: {
    FormRange
  },
  props: {
    ship: Object,
    step: Number
  },
  methods: {
    onPaste: function(row, col, evt) {
      console.log(row, col, evt);
      console.log("pasted");
    },
    onStepChange: function(dir){
      this.$emit('onStepChanged',dir)
    },
    handleShipTypeChange: function(evt) {
      this.$emit('onShipTypeChanged',evt.target.value);
    }
  },
  watch: {
    ship: {
      handler: function(val, oldVal) {
        this.ship.particulars.Loa = Math.min(
          Math.max(this.vRange.LoaMin, this.ship.particulars.Loa),
          this.vRange.LoaMax
        );
        this.ship.particulars.Lpp = Math.min(
          Math.max(this.vRange.LppMin, this.ship.particulars.Lpp),
          this.vRange.LppMax
        );
        this.ship.particulars.Lwl = Math.min(
          Math.max(this.vRange.LwlMin, this.ship.particulars.Lwl),
          this.vRange.LwlMax
        );
        this.ship.particulars.B = Math.min(
          Math.max(this.vRange.BMin, this.ship.particulars.B),
          this.vRange.BMax
        );
        this.ship.particulars.T = Math.min(
          Math.max(this.vRange.TMin, this.ship.particulars.T),
          this.vRange.TMax
        );
        this.ship.particulars.D = Math.min(
          Math.max(this.vRange.DMin, this.ship.particulars.D),
          this.vRange.DMax
        );
      },
      deep: true
    },
    sp: {
      handler: function(val, oldVal) {}
    }
  },
  computed: {
    vRange: function() {
      return {
        LoaMin: 80,
        LoaMax: 350,
        LwlMin: Math.round(0.90 * this.ship.particulars.Loa * 10) / 10,
        LwlMax: Math.round(1.0 * this.ship.particulars.Loa * 10) / 10,
        LppMin: Math.round(0.90 * this.ship.particulars.Lwl * 10) / 10,
        LppMax: Math.round(0.98 * this.ship.particulars.Lwl * 10) / 10,
        BMin: Math.round((this.ship.particulars.Loa / 9.5) * 10) / 10,
        BMax: Math.round((this.ship.particulars.Loa / 3.9) * 10) / 10,
        TMin: Math.round((this.ship.particulars.B / 6) * 10) / 10,
        TMax: Math.round((this.ship.particulars.B / 2) * 10) / 10,
        TballastMin: 0.5*Math.round((this.ship.particulars.B / 6) * 10) / 10,
        TballastMax: this.ship.particulars.T,
        DMin: Math.round(1.2 * this.ship.particulars.T * 10) / 10,
        DMax: ( (this.ship.type === 'tanker' || this.ship.type === 'bulk carrier') ? Math.round(2.5 * this.ship.particulars.T * 10) / 10 : Math.round( 0.175 * this.ship.particulars.Lpp * 10) / 10 ),
        SMin: 0.5*(this.ship.particulars.Loa*this.ship.particulars.B+2*(this.ship.particulars.Loa*this.ship.particulars.T+this.ship.particulars.B*this.ship.particulars.T)),
        SMax: this.ship.particulars.Loa*this.ship.particulars.B+2*(this.ship.particulars.Loa*this.ship.particulars.T+this.ship.particulars.B*this.ship.particulars.T),
        DpropMin: Math.round(0.5*this.ship.particulars.T),
        DpropMax: 0.9*this.ship.particulars.T
      };
    },
    sp: function() {
      return this.ship.speedPower;
    }
  }
};
</script>

<style scoped>
.alert-light {
  border-color: #c6c8ca;
  margin-bottom: 0;
}
</style>